import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import {
  FaDatabase,
  FaGlobe,
  FaPlus,
  FaRedo,
  FaSpinner,
} from "react-icons/fa";
import Select from "react-select";
import { toast } from "react-toastify";

import DomainTable from "./DomainTable";
import Modal from "./Modal";
import { GlobalContext } from "../../context/GlobalContext";

/**
 * Reusable Bootstrap form field
 */
const FormField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  disabled = false,
}) => {
  return (
    <div className="mb-3">
      <label htmlFor={name} className="form-label fw-semibold">
        {label}

        {required && (
          <span className="text-danger ms-1">*</span>
        )}
      </label>

      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        className="form-control form-control-lg"
      />
    </div>
  );
};

export default function DomainPage() {
  const { API_URL } = useContext(GlobalContext);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [domains, setDomains] = useState([]);
  const [domainURL, setDomainURL] = useState([]);

  const [loading, setLoading] = useState(true);
  const [creatingDatabase, setCreatingDatabase] = useState(false);

  const [showCreateDbModal, setShowCreateDbModal] =
    useState(false);

  const [createDbForm, setCreateDbForm] = useState({
    domain: "",
    newDomain: "",
    api_key: "",
    databaseName: "",
  });

  // ---------------------------------------------------------------------------
  // Load domains
  // ---------------------------------------------------------------------------

  const loadDomains = useCallback(async () => {
    setLoading(true);

    try {
      const [domainsResponse, domainUrlResponse] =
        await Promise.all([
          axios.get(`${API_URL}/domains`),
          axios.get(`${API_URL}/collectionsDomainURL`),
        ]);

      // Domains used by the main table
      const loadedDomains = Array.isArray(domainsResponse?.data) ? domainsResponse.data : [];
      // Domains used by the dropdown
      const collectionDomains = Array.isArray(domainUrlResponse?.data) ? domainUrlResponse.data : [];
      const uniqueDomains = [...new Set(collectionDomains.map((item) => item?.domain)
            .filter(Boolean)),].map((domain) => ({
        domain,
      }));

      setDomains(loadedDomains);
      setDomainURL(uniqueDomains);

      return true;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to load domains";

      toast.error(message);

      return false;
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // ---------------------------------------------------------------------------
  // Initial load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  // ---------------------------------------------------------------------------
  // Domain dropdown options
  // ---------------------------------------------------------------------------

  const domainOptions = useMemo(() => {
    return domainURL.map(({ domain }) => ({
      value: domain,
      label: domain,
    }));
  }, [domainURL]);

  // ---------------------------------------------------------------------------
  // Refresh
  // ---------------------------------------------------------------------------

  const handleRefresh = async () => {
    const success = await loadDomains();

    if (success) {
      toast.success("Domains refreshed successfully");
    }
  };

  // ---------------------------------------------------------------------------
  // Open Create Database modal
  // ---------------------------------------------------------------------------

  const openCreateDbModal = () => {
    setCreateDbForm({
      domain: "",
      newDomain: "",
      api_key: "",
      databaseName: "",
    });

    setShowCreateDbModal(true);
  };

  // ---------------------------------------------------------------------------
  // Close Create Database modal
  // ---------------------------------------------------------------------------

  const closeCreateDbModal = () => {
    if (creatingDatabase) {
      return;
    }

    setShowCreateDbModal(false);

    setCreateDbForm({
      domain: "",
      newDomain: "",
      api_key: "",
      databaseName: "",
    });
  };

  // ---------------------------------------------------------------------------
  // Handle form input
  // ---------------------------------------------------------------------------

  const handleDbFormChange = (event) => {
    const { name, value } = event.target;

    setCreateDbForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ---------------------------------------------------------------------------
  // Handle domain dropdown
  // ---------------------------------------------------------------------------

  const handleDomainChange = (selectedOption) => {
    const selectedDomain = selectedOption?.value || "";

    setCreateDbForm((prev) => ({
      ...prev,

      // Existing selected domain
      domain: selectedDomain,

      // Clear manually entered domain
      newDomain: "",
    }));
  };

  // ---------------------------------------------------------------------------
  // Create database
  // ---------------------------------------------------------------------------

  const submitCreateDb = async (event) => {
    event?.preventDefault();

    const domain = createDbForm.domain.trim() || createDbForm.newDomain.trim();
    const apiKey = createDbForm.api_key.trim();
    const databaseName = createDbForm.databaseName.trim();

    if (!domain) {
      toast.error("Please select a domain or enter a new domain");
      return;
    }

    if (!apiKey) {
      toast.error("Please enter API key");
      return;
    }

    if (!databaseName) {
      toast.error("Please enter database name");
      return;
    }

    setCreatingDatabase(true);

    try {
      const response = await axios.post(`${API_URL}/create-new-database`,
        {
          domain,
          api_key: apiKey,
          database_name: databaseName,
        }
      );

      const result = response?.data || {};

      toast.success(result?.message || result.error);

      // Close modal
      setShowCreateDbModal(false);

      // Reset form
      setCreateDbForm({
        domain: "",
        newDomain: "",
        api_key: "",
        databaseName: "",
      });

      // Refresh table/dropdown
      await loadDomains();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to create database";

      toast.error(message);
    } finally {
      setCreatingDatabase(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Selected domain
  // ---------------------------------------------------------------------------

  const selectedDomainOption =
    domainOptions.find(
      (option) =>
        option.value === createDbForm.domain
    ) || null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="container-fluid py-4">
      {/* Page Header */}

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        {/* Page title */}
        <div>
          <h4 className="mb-1 d-flex align-items-center gap-2 fw-bold">
            <FaGlobe className="text-primary" />
            Domains &amp; Databases
          </h4>

          <p className="text-muted mb-0">
            Manage domains, databases, and their configurations.
          </p>
        </div>

        {/* Header actions */}
        <div className="d-flex align-items-center gap-2">
          {/* Refresh */}
          <button
            type="button"
            className="btn btn-outline-secondary d-flex align-items-center fs-5 rounded-pill gap-2 py-2 px-3"
            onClick={handleRefresh}
            disabled={loading}
          >
            <FaRedo
              className={loading ? "spin" : ""}
            />

            Refresh
          </button>

          {/* Add database */}
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2 px-3 py-2 fs-5 rounded-pill"
            onClick={openCreateDbModal}
          >
            <FaPlus />

            Add New
          </button>
        </div>
      </div>

      {/* Domain Table */}
      <DomainTable
        domains={domains}
        loading={loading}
      />

      {/* Create Database Modal */}
      <Modal
        title="Add New Database"
        show={showCreateDbModal}
        onClose={closeCreateDbModal}
        footer={
          <>
            {/* Cancel */}
            <button
              type="button"
              className="btn btn-light border px-4"
              onClick={closeCreateDbModal}
              disabled={creatingDatabase}
            >
              Cancel
            </button>

            {/* Create */}
            <button
              type="submit"
              form="create-database-form"
              className="btn btn-primary d-flex align-items-center gap-2 px-4"
              disabled={creatingDatabase}
            >
              {creatingDatabase && (<FaSpinner className="spin" />)}
              {creatingDatabase ? "Creating..." : "Create Database"}
            </button>
          </>
        }
      >
        <form
          id="create-database-form"
          onSubmit={submitCreateDb}
        >
          {/* Domain */}
          <div className="mb-4">
            <label className="form-label fw-semibold">
              Domain
              <span className="text-danger ms-1">
                *
              </span>
            </label>

            <Select
              options={domainOptions}
              value={selectedDomainOption}
              onChange={handleDomainChange}
              placeholder="Select an existing domain..."
              isClearable
              isSearchable
              isDisabled={creatingDatabase}
              noOptionsMessage={() => "No existing domains found"}
              classNamePrefix="react-select"
            />

            {/* New Domain Input */}
            {!createDbForm.domain && (
              <div className="mt-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h6 className="fw-semibold">
                    Or add a new domain
                  </h6>
                  <div className="flex-grow-1 border-top" />
                </div>

                <div className="input-group input-group-lg">
                  <span className="input-group-text bg-light">
                    <FaGlobe className="text-primary" />
                  </span>

                  <input
                    type="text"
                    name="newDomain"
                    value={createDbForm.newDomain}
                    onChange={handleDbFormChange}
                    className="form-control"
                    placeholder="e.g. fssai.gov.in"
                    disabled={creatingDatabase}
                    autoComplete="off"
                  />
                </div>

                <small className="text-muted">
                  Enter a new domain if it is not available
                  in the dropdown.
                </small>
              </div>
            )}
          </div>

          {/* ============================================================ */}
          {/* API Key */}
          {/* ============================================================ */}

          <FormField
            label="API Key"
            name="api_key"
            value={createDbForm.api_key}
            onChange={handleDbFormChange}
            placeholder="e.g. fssai"
            required
            disabled={creatingDatabase}
          />

          {/* ============================================================ */}
          {/* Database Name */}
          {/* ============================================================ */}

          <FormField
            label="Database Name"
            name="databaseName"
            value={createDbForm.databaseName}
            onChange={handleDbFormChange}
            placeholder="e.g. fssaidb"
            required
            disabled={creatingDatabase}
          />

          {/* ============================================================ */}
          {/* Configuration Preview */}
          {/* ============================================================ */}

          <div className="border rounded-3 bg-light p-3 mt-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <div
                className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
                style={{
                  width: "38px",
                  height: "38px",
                  minWidth: "38px",
                }}
              >
                <FaDatabase className="text-primary" />
              </div>

              <div>
                <div className="fw-semibold">
                  Database Configuration
                </div>

                <small className="text-muted">
                  Review the configuration before creating.
                </small>
              </div>
            </div>

            <div className="row g-2 mt-2">
              {/* Domain preview */}
              <div className="col-md-6">
                <div className="bg-white border rounded-2 p-2">
                  <small className="text-muted d-block">
                    Domain
                  </small>

                  <span className="fw-semibold text-dark">
                    {createDbForm.domain ||
                      createDbForm.newDomain ||
                      "—"}
                  </span>
                </div>
              </div>

              {/* Database preview */}
              <div className="col-md-6">
                <div className="bg-white border rounded-2 p-2">
                  <small className="text-muted d-block">
                    Database
                  </small>

                  <span className="fw-semibold text-dark">
                    {createDbForm.databaseName || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}

